/**
 * PayRatesPanel — "My pay rates" section on /billing/kitchen (pricing
 * rework Phase E).
 *
 * Read-only table of what Ordo pays the kitchen per unit for every
 * item on the vendor's active menus: Item | School | Menu | Pay per
 * unit | Upcoming. Upcoming renders the next scheduled rate change as
 * "$2.10 → $2.25 from Sep 1".
 *
 * The payload is AP-side only — the server never sends customer
 * prices or margin to this surface. Loading state is a
 * block-for-block skeleton (never a "Loading…" string); empty state
 * renders when the vendor has no active menus / items.
 */

import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'

import { type KitchenPayRateRow, useBillingKitchenPayRatesQuery } from '@/api/billing-kitchen'

import { formatCents } from './kitchen-payouts-helpers'

export interface PayRatesPanelProps {
  /** Vendor narrow forwarded to the query (matches the page's active vendor). */
  servicingVendorId?: string | null
}

export function PayRatesPanel({ servicingVendorId }: PayRatesPanelProps) {
  const { data, isPending, error } = useBillingKitchenPayRatesQuery({ servicingVendorId: servicingVendorId ?? null })

  return (
    <Card>
      <CardHeader className="flex flex-col gap-1.5">
        <h3 className="text-lg font-semibold tracking-tight">My pay rates</h3>
        <p className="text-xs text-muted-foreground">
          What Ordo pays your kitchen per unit for each item on your active menus, including scheduled rate changes.
        </p>
      </CardHeader>
      <CardContent>
        {isPending && !data ? (
          <PayRatesRowsSkeleton />
        ) : error || !data || data.rates.length === 0 ? (
          <EmptyState hadError={Boolean(error)} />
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead>Menu</TableHead>
                  <TableHead className="text-right">Pay per unit</TableHead>
                  <TableHead>Upcoming</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rates.map((rate) => (
                  <PayRateTableRow key={`${rate.menuId}:${rate.menuItemId}`} rate={rate} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

function PayRateTableRow({ rate }: { rate: KitchenPayRateRow }) {
  return (
    <TableRow>
      <TableCell className="text-sm font-medium">{rate.itemName}</TableCell>
      <TableCell className="whitespace-nowrap text-sm">{rate.schoolName}</TableCell>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{rate.menuName}</TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {rate.isCosted ? formatCents(rate.vendorPayCents, rate.currency) : 'No rate'}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{upcomingText(rate)}</TableCell>
    </TableRow>
  )
}

/**
 * "$2.10 → $2.25 from Sep 1" for the soonest scheduled change; "—"
 * when no future-dated rate exists. Exported shape stays local — pure
 * presentation.
 */
function upcomingText(rate: KitchenPayRateRow): string {
  const next = rate.upcoming[0]
  if (!next) {
    return '—'
  }
  const current = rate.isCosted ? formatCents(rate.vendorPayCents, rate.currency) : '—'
  return `${current} → ${formatCents(next.vendorPayCents, rate.currency)} from ${formatEffectiveFrom(next.effectiveFrom)}`
}

function formatEffectiveFrom(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function PayRatesRowsSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading pay rates">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b pb-3 last:border-b-0">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
          <div className="flex-1" />
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  )
}

function EmptyState({ hadError }: { hadError: boolean }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm">
      <p className="font-medium text-foreground">{hadError ? "Couldn't load pay rates" : 'No pay rates yet'}</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        {hadError
          ? 'Something went wrong loading your rates. Try again in a moment.'
          : 'Rates appear here once your kitchen serves an active menu with authored per-item pay.'}
      </p>
    </div>
  )
}
